import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { Save, Plus, Trash2, Eye, Users } from 'lucide-react';

const EVALUATOR_ROLES = [
  'School Psychologist', 'SLP', 'Special Education Teacher',
  'Occupational Therapist', 'Physical Therapist', 'General Education Teacher',
  'Board Certified Behavior Analyst', 'Other',
];

function mergeContent(existing) {
  const defaults = { evaluator_observations: [] };
  if (!existing || typeof existing !== 'object') return defaults;
  // Migration from old flat structure
  if (existing.testing_environment && !existing.evaluator_observations) {
    const name = existing.evaluators || '';
    const parts = [
      existing.testing_environment && `Testing Environment: ${existing.testing_environment}`,
      existing.attention_effort && `Attention / Effort: ${existing.attention_effort}`,
      existing.behavior_cooperation && `Behavior / Cooperation: ${existing.behavior_cooperation}`,
      existing.validity_statement && `Validity: ${existing.validity_statement}`,
      existing.additional_notes && `Additional Notes: ${existing.additional_notes}`,
    ].filter(Boolean);
    return {
      evaluator_observations: [{
        id: `obs_migrated_${Date.now()}`,
        name: name.split(';')[0]?.trim() || '',
        role: '',
        observation: parts.join('\n\n'),
      }],
    };
  }
  return { ...defaults, ...existing };
}

export default function ObservationEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const handleSave = () => onSave(content);
  const observations = content.evaluator_observations || [];

  const addObservation = () => {
    setContent(prev => ({
      ...prev,
      evaluator_observations: [
        ...(prev.evaluator_observations || []),
        { id: `obs_${Date.now()}`, name: '', role: '', observation: '' },
      ],
    }));
  };

  const removeObservation = (idx) => {
    setContent(prev => ({
      ...prev,
      evaluator_observations: prev.evaluator_observations.filter((_, i) => i !== idx),
    }));
  };

  const updateObservation = (idx, field, value) => {
    setContent(prev => ({
      ...prev,
      evaluator_observations: prev.evaluator_observations.map((obs, i) =>
        i === idx ? { ...obs, [field]: value } : obs
      ),
    }));
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="observation-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="observation-title">Observation: Testing Environment</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <Badge className="bg-[#5A8A5A]/10 text-[#5A8A5A] text-xs mt-2">{observations.length} evaluator{observations.length !== 1 ? 's' : ''}</Badge>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="observation-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
        <Eye className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700">
          <p className="font-medium mb-1">Each evaluator records their own testing observations.</p>
          <p>Include testing environment, student attention/effort/engagement, behavior/cooperation/regulation, fatigue if relevant, and whether results are a valid representation of ability.</p>
        </div>
      </div>

      {observations.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
          <Users className="h-8 w-8 text-[#E5E5E5] mx-auto mb-3" />
          <p className="text-sm text-[#595959] mb-4">No evaluator observations added yet.</p>
          <Button variant="outline" className="rounded-xl" onClick={addObservation} data-testid="add-first-observation">
            <Plus className="h-4 w-4 mr-2" />Add Evaluator Observation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {observations.map((obs, idx) => (
            <div key={obs.id || idx} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`evaluator-obs-${idx}`}>
              <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#5A8A5A]" />
                  <span className="text-sm font-medium text-[#1C1C1C]">Evaluator {idx + 1}</span>
                  {obs.name && <span className="text-xs text-[#595959]">— {obs.name}</span>}
                </div>
                <button onClick={() => removeObservation(idx)}
                  className="p-1.5 rounded-lg hover:bg-red-50 transition" data-testid={`remove-obs-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Evaluator Name</label>
                    <Input value={obs.name || ''} onChange={e => updateObservation(idx, 'name', e.target.value)}
                      placeholder="e.g., Dr. Sarah Martinez" className="rounded-xl border-[#E5E5E5] text-sm h-9"
                      data-testid={`obs-name-${idx}`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Role / Credentials</label>
                    <Select value={obs.role || ''} onValueChange={v => updateObservation(idx, 'role', v)}>
                      <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`obs-role-${idx}`}>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVALUATOR_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Observation Narrative</label>
                  <p className="text-[11px] text-[#595959] mb-2">
                    Describe the testing environment, student's attention/effort/engagement, behavior/cooperation/regulation, any fatigue, and whether results are a valid representation of ability.
                  </p>
                  <Textarea value={obs.observation || ''} onChange={e => updateObservation(idx, 'observation', e.target.value)}
                    placeholder="Describe your observations during the evaluation session(s)..."
                    rows={8} className="rounded-xl border-[#E5E5E5] text-sm"
                    data-testid={`obs-narrative-${idx}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button variant="outline" className="rounded-xl" onClick={addObservation} data-testid="add-observation-btn">
        <Plus className="h-4 w-4 mr-2" />Add Evaluator Observation
      </Button>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="observation-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Observations'}
        </Button>
      </div>
    </div>
  );
}
